import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { ProgressSpinner } from "primereact/progressspinner";

import PlanPhasesCard from "app/pages/InteractiveProposal/menuPages/Summary/PlanPhasesCard";
import FinancingCard from "app/pages/InteractiveProposal/menuPages/Summary/FinancingCard";
import PolicyInformationCard from "../components/PolicyInformationCard";
import OutsidePledgedAssetsCard from "../components/OutsidePledgedAssetsCard";
import YearStepper, { YearStep } from "app/pages/InteractiveProposal/YearStepper";

// ─── API response types ────────────────────────────────────────────────────────

interface PlanReview {
  year: number;
  planYear: number;
  reviewCompleted: string | null;
  confirmedBy: number;
}

interface ReviewCenterSummaryData {
  planType: string;
  planSubType: string;
  planEffectiveDate: string;
  proposalNo: string;
  entity: { title: string; logoUrl: string };
  contacts: { profile: { firstName: string; lastName: string } };
  phases: {
    funding: { completed: number; total: number; progress: number };
    growth: { completed: number; total: number; progress: number };
  };
  selectedYear: number;
  selectedCreditType: string;
  planReviews: PlanReview[];
  financing: {
    lender: { logoUrl: string; name: string };
    financingNum: string;
    borrower: { title: string; logoUrl: string };
    interestTreatment: string;
    expectedPayoff: string;
    initialRate: number;
    financingValue: number;
    rateTerms: string;
  };
  policies: Array<{
    insured: { user: { profile: { firstName: string; lastName: string; profilePicture: string } } };
    policyNumber: string;
    PolicyReview: Array<{
      policyAccountValue: number;
      policySurrenderValue: number;
      policyDB: number;
    }>;
    beneficiary: string;
  }>;
  outsideAssetsLedger: Array<{
    id: string;
    type: string;
    institution: string;
    accountNumber: string;
    value: number;
    creditPercentage: number;
    creditValue: number;
  }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (value: number): string => {
  if (value == null) return "—";
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const titleCase = (str: string) =>
  str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCreditTypeLabel = (ct: string) => (ct === "postCredit" ? "Post-Credit" : "Pre-Credit");

// ─── Map API response to YearStep[] ───────────────────────────────────────────

function mapReviewsToSteps(
  reviews: PlanReview[],
  selectedYear: number,
  selectedCreditType: string,
): YearStep[] {
  return reviews.map((r) => {
    if (r.planYear === selectedYear) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "CURRENT",
        label: formatCreditTypeLabel(selectedCreditType),
      };
    }
    if (!r.reviewCompleted && r.confirmedBy === 0) {
      return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
    }
    if (r.confirmedBy > 0) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "COMPLETED",
        label: `Confirm (${r.confirmedBy})`,
      };
    }
    return {
      yearNumber: r.planYear,
      calendarYear: r.year,
      status: "SKIPPED",
      label: `Skip (${r.planYear})`,
    };
  });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ReviewCenterOverview = () => {
  const planId = getSessionStore("planInfo")?.planId;
  // const planId = "43adc439-dbfc-4b7c-a7c8-d1798d9bb7de";
  const { pathname } = useLocation();
  const isReviewSummary = pathname.includes("interactive-review-center/summary");

  const [selectedYear, setSelectedYear] = useState(1);
  const [selectedCreditType] = useState("preCredit");
  const [summaryData, setSummaryData] = useState<ReviewCenterSummaryData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!planId) return;

    setLoading(true);
    makeRequest("interactiveReviewCenterSummary")
      .get(`${planId}?year=${selectedYear}&creditType=${selectedCreditType}`, true)
      .then((res: any) => {
        const data: ReviewCenterSummaryData = res?.data?.data;
        setSummaryData(data);
        console.log("Review Center Summary:", data);
      })
      .catch((err: any) => {
        console.error("Failed to fetch review center summary:", err);
      })
      .finally(() => setLoading(false));
  }, [planId, selectedYear, selectedCreditType]);

  // ── Map API data to component props ──────────────────────────────────────────
  const mockYearSteps: YearStep[] = [
    { yearNumber: 1, calendarYear: 2022, status: "COMPLETED", label: "Confirm (All)" },
    { yearNumber: 2, calendarYear: 2023, status: "SKIPPED", label: "Skip (2)" },
    { yearNumber: 3, calendarYear: 2024, status: "COMPLETED", label: "Confirm (1)" },
    { yearNumber: 4, calendarYear: 2025, status: "CURRENT", label: "Pre-Credit" },
    { yearNumber: 5, calendarYear: 2026, status: "LOCKED" },
  ];

  const yearSteps: YearStep[] = summaryData
    ? mapReviewsToSteps(summaryData.planReviews, selectedYear, selectedCreditType)
    : mockYearSteps;

  const planPhasesProps = summaryData
    ? {
        planType: summaryData.planType,
        effectiveDate: formatDate(summaryData.planEffectiveDate),
        progressPercent: summaryData.phases.funding.progress,
        phases: [
          {
            label: "FUNDING",
            current: summaryData.phases.funding.completed,
            total: summaryData.phases.funding.total,
            progress: summaryData.phases.funding.progress,
            isActive: true,
          },
          {
            label: "GROWTH",
            current: summaryData.phases.growth.completed,
            total: summaryData.phases.growth.total,
            progress: summaryData.phases.growth.progress,
            isActive: false,
          },
        ],
      }
    : undefined;

  const financingRows = summaryData
    ? [
        {
          id: "1",
          lenderName: summaryData.financing.lender.name,
          lenderLogo: summaryData.financing.lender.logoUrl,
          financingNumber: summaryData.financing.financingNum,
          borrowerName: summaryData.financing.borrower.title,
          borrowerLogo: summaryData.financing.borrower.logoUrl,
          interestTreatment: titleCase(summaryData.financing.interestTreatment),
          initialRate: `${summaryData.financing.initialRate}%`,
          financingValue: formatCurrency(summaryData.financing.financingValue),
          expectedPayoff: formatDate(summaryData.financing.expectedPayoff),
        },
      ]
    : undefined;

  const policyRows = summaryData
    ? summaryData.policies.map((p, i) => ({
        id: p.policyNumber || String(i),
        insuredName: `${p.insured.user.profile.firstName} ${p.insured.user.profile.lastName}`,
        insuredAvatar: p.insured.user.profile.profilePicture,
        policyNumber: p.policyNumber,
        accountValue: formatCurrency(p.PolicyReview?.[0]?.policyAccountValue),
        surrenderValue: formatCurrency(p.PolicyReview?.[0]?.policySurrenderValue),
        benefit: formatCurrency(p.PolicyReview?.[0]?.policyDB),
        ownerName: summaryData.entity.title,
        ownerLogo: summaryData.entity.logoUrl,
        beneficiary: p.beneficiary,
      }))
    : undefined;

  const assetRows = summaryData
    ? summaryData.outsideAssetsLedger.map((a) => ({
        id: a.id,
        type: titleCase(a.type),
        institution: a.institution,
        accountId: a.accountNumber,
        value: formatCurrency(a.value),
        creditPercent: `${a.creditPercentage}%`,
        creditValue: formatCurrency(a.creditValue),
      }))
    : undefined;

  return (
    <div className="flex flex-col gap-2 pb-6 mt-[180px] relative">
      {loading && (
        <div className="flex justify-center py-8">
          <ProgressSpinner style={{ width: 40, height: 40 }} />
        </div>
      )}

      {isReviewSummary && (
        <YearStepper steps={yearSteps} onYearClick={(step) => setSelectedYear(step.yearNumber)} />
      )}

      <PlanPhasesCard {...(planPhasesProps ?? {})} />

      <FinancingCard rows={financingRows} />

      <PolicyInformationCard policies={policyRows} />

      <OutsidePledgedAssetsCard assets={assetRows} />
    </div>
  );
};

export default ReviewCenterOverview;
